/**
 * The facility navigator's URL state — the PURE core of the hand-rolled "router" (ADR-023). Kept deliberately free of
 * React and of `window`, so the whole precedence + param-clearing model is one exhaustively-unit-testable reducer instead
 * of imperative `p.set()`/`p.delete()` calls scattered across writers (the fragility both review models flagged: add a
 * param, forget one `delete`, leak state → impossible UI overlaps).
 *
 * `useFacilityNavParams` is the thin React shell over this: parse the query → {@link NavState}, dispatch a semantic
 * {@link NavAction} → {@link navReducer} → serialize back → `history.push/replaceState`. The **query string is the state**
 * (ADR-014); there are no path segments (tenancy is host/edge-resolved, so deep-links never need edge path-rewrites).
 */

/** The active tab of the global search view (`arc_stab`). */
export type SearchTab = "facilities" | "manuals" | "descriptions";

/** The pages of the "Dashboard & settings" workspace (`arc_view`, ADR-032) — the legacy `arc_dashboard_view` states, one
 *  kebab-case value each (facilityManagement dropped: facility lifecycle lives on the browse screen). A page's existence
 *  here is routing only; whether it is BUILT or VISIBLE to the caller is the workspace registry's concern. */
export const WORKSPACE_VIEWS = [
  "overview",
  "profile",
  "tasks",
  "history",
  "manuals",
  "favourites",
  "favourite-facilities",
  "ratings",
  "roles",
  "qr",
  "sensors",
  "generic-user",
] as const;
export type WorkspaceView = (typeof WORKSPACE_VIEWS)[number];

/** A known workspace page, or null for anything else (absent, unknown, wrong case) — an unknown deep-link is not a crash. */
export function parseView(v: string | null): WorkspaceView | null {
  return v !== null && (WORKSPACE_VIEWS as readonly string[]).includes(v) ? (v as WorkspaceView) : null;
}

/** The complete, VALIDATED nav state — one field per `arc_*` query param. `null` ⇒ the param is absent. Views are DERIVED
 *  from this (precedence lives in the reader/`FacilityDrilldown`), so several fields can be set at once on purpose (e.g. a
 *  drilled `selectedId` UNDER an open `searchQuery`). */
export interface NavState {
  /** `arc_fac_selected` — the drilled-into facility (absent ⇒ the root list). */
  selectedId: number | null;
  /** `arc_facility` — the facility whose MANUALS view is open. */
  manualsId: number | null;
  /** `arc_manual` — a manual open in the full-screen reader (a clean top-level route; takes precedence when set). */
  openManualId: number | null;
  /** `arc_edit` — a manual open in the full-screen AUTHORING editor (ADR-002 Phase 10; the HIGHEST-precedence full-screen
   *  route — an editor wins over the reader). */
  editManualId: number | null;
  /** `arc_details` — a manual whose DETAILS DRAWER should open over the `arc_facility` manuals view (a linked-manual
   *  card's navigation, PARITY-GAPS §2). ONE-SHOT: the view consumes it (`dropStale`) once the drawer is open, so a
   *  refresh lands on the plain list and Back never re-opens a drawer. */
  detailsManualId: number | null;
  /** `arc_search` — the active global-search query (trimmed; "" ⇒ null). */
  searchQuery: string | null;
  /** `arc_stab` — the active search tab. */
  searchTab: SearchTab | null;
  /** `arc_view` — the "Dashboard & settings" workspace page (ADR-032): a TOP-LEVEL route rendered above the realm split,
   *  winning over every other view (legacy parity: the dashboard took priority). Opening it KEEPS every other param, so
   *  closing restores exactly what was visible underneath. */
  view: WorkspaceView | null;
  /** `arc_role` — the role selected in Settings ▸ Manage roles (ADR-030 Phase D): a shareable deep link into one
   *  role's editor (the legacy dashboard hints linked straight to a role). Meaningful only with `arc_view=roles`;
   *  leaving the roles page drops it. */
  roleId: number | null;
}

/** The semantic navigations. Each maps 1:1 to a `useFacilityNavParams` writer; the reducer owns which params each clears. */
export type NavAction =
  | { type: "drillTo"; id: number | null }
  // `replace` (default false) collapses the current entry instead of pushing — used when EXITING the reader back to the
  // list, so browser Back can't re-open the full-screen reader (panel consensus).
  | { type: "openManuals"; id: number; replace?: boolean }
  // A facility's manuals view WITH one manual's details drawer open (the linked-manual card's navigation).
  // `replace` collapses the current entry — a link to a manual in the SAME facility would otherwise push a twin entry
  // (the param is consumed right after), making the first browser Back a dead press (panel).
  | { type: "openManualDetails"; facilityId: number; manualId: number; replace?: boolean }
  | { type: "viewManual"; id: number }
  | { type: "editManual"; id: number }
  | { type: "closeManuals" }
  | { type: "openSearch"; query: string }
  | { type: "closeSearch" }
  | { type: "setSearchTab"; tab: SearchTab }
  | { type: "dropStale"; key: "arc_fac_selected" | "arc_facility" | "arc_manual" | "arc_edit" | "arc_details" | "arc_role" }
  // The workspace (ADR-032): open = push (one entry for the whole visit), a page switch inside it = `replace`, close =
  // replace (strip only `arc_view`) — the reader/editor's history model. No `dropStale` key: `closeView` IS the strip.
  | { type: "openView"; view: WorkspaceView; replace?: boolean }
  | { type: "closeView" }
  // The role selected in Manage roles (replace — a click per role must not grow history).
  | { type: "selectRole"; id: number | null };

/** A positive int4 id, or null for anything else (absent, non-numeric, ≤0, overflow) — mirrors `FacilityIdSchema`. */
export function parseId(v: string | null): number | null {
  if (v === null || !/^\d+$/.test(v)) return null;
  const n = Number(v);
  return Number.isInteger(n) && n > 0 && n <= 2147483647 ? n : null;
}

function parseTab(v: string | null): SearchTab | null {
  return v === "facilities" || v === "manuals" || v === "descriptions" ? v : null;
}

/**
 * Parse a raw `location.search` into a VALIDATED {@link NavState}. Per-field validation with a safe fallback (a bad id /
 * unknown tab ⇒ null), so a hand-edited or stale URL can never crash a consumer or select an impossible tab. Combinations
 * are intentionally NOT normalised here — the layered params (drill under search, etc.) are meaningful; precedence is a
 * RENDER concern, applied by the views.
 */
export function parseNavState(search: string): NavState {
  const p = new URLSearchParams(search);
  return {
    selectedId: parseId(p.get("arc_fac_selected")),
    manualsId: parseId(p.get("arc_facility")),
    openManualId: parseId(p.get("arc_manual")),
    editManualId: parseId(p.get("arc_edit")),
    detailsManualId: parseId(p.get("arc_details")),
    searchQuery: (p.get("arc_search") ?? "").trim() || null,
    searchTab: parseTab(p.get("arc_stab")),
    view: parseView(p.get("arc_view")),
    roleId: parseId(p.get("arc_role")),
  };
}

/**
 * The pure navigation reducer — the single place precedence + "clear the incompatible params" is decided. Every writer
 * dispatches one of these; the mapping to what each clears is here, not duplicated across callbacks.
 */
export function navReducer(state: NavState, action: NavAction): NavState {
  switch (action.type) {
    // Drilling leaves the manuals view, the reader, the EDITOR AND any open search (a fresh browse position).
    case "drillTo":
      return { selectedId: action.id, manualsId: null, openManualId: null, editManualId: null, detailsManualId: null, searchQuery: null, searchTab: null, view: null, roleId: null };
    // Open a facility's manuals list. KEEP any search + tab + drill selection UNDER it (so the manuals Back returns to
    // the same search); clear the reader + editor — this opens the LIST, not a specific manual.
    case "openManuals":
      return { ...state, manualsId: action.id, openManualId: null, editManualId: null, detailsManualId: null, view: null, roleId: null };
    // The manuals view of `facilityId` with `manualId`'s drawer open — otherwise exactly `openManuals`.
    case "openManualDetails":
      return { ...state, manualsId: action.facilityId, detailsManualId: action.manualId, openManualId: null, editManualId: null, view: null, roleId: null };
    // Open the reader as a clean top-level route: set `arc_manual`, clear the manuals view + editor + search (keep the drill
    // selection underneath). This is what makes `?arc_manual=<id>` a bare, shareable URL with no facility-list flash.
    case "viewManual":
      return { ...state, openManualId: action.id, editManualId: null, manualsId: null, detailsManualId: null, searchQuery: null, searchTab: null, view: null, roleId: null };
    // Open the AUTHORING editor as a clean top-level route (the highest-precedence full-screen view — it wins over the
    // reader). Mirrors viewManual: set `arc_edit`, clear the reader + manuals view + search (keep the drill underneath).
    case "editManual":
      return { ...state, editManualId: action.id, openManualId: null, manualsId: null, detailsManualId: null, searchQuery: null, searchTab: null, view: null, roleId: null };
    case "closeManuals":
      return { ...state, manualsId: null, openManualId: null, editManualId: null, detailsManualId: null };
    // The search is a top-level view — never behind the manuals view / reader / editor. A `#`-prefixed (description) query
    // clears any sticky tab so the view re-derives to Descriptions (else a prior Manuals tab would run a NAME search).
    case "openSearch": {
      const q = action.query.trim();
      return {
        ...state,
        manualsId: null,
        openManualId: null,
        editManualId: null,
        detailsManualId: null,
        searchQuery: q || null,
        searchTab: q.startsWith("#") ? null : state.searchTab,
        view: null,
        roleId: null,
      };
    }
    case "closeSearch":
      return { ...state, searchQuery: null, searchTab: null };
    case "setSearchTab":
      return { ...state, searchTab: action.tab };
    // Drop a single stale/consumed id (data resolved to "missing", or a one-shot param consumed).
    case "dropStale":
      return {
        ...state,
        selectedId: action.key === "arc_fac_selected" ? null : state.selectedId,
        manualsId: action.key === "arc_facility" ? null : state.manualsId,
        openManualId: action.key === "arc_manual" ? null : state.openManualId,
        editManualId: action.key === "arc_edit" ? null : state.editManualId,
        // A drawer target is meaningless without its list: a stale-dropped `arc_facility` (archived / not visible) takes
        // `arc_details` with it — else the one-shot param would pin on the root list with no view to consume it.
        detailsManualId: action.key === "arc_details" || action.key === "arc_facility" ? null : state.detailsManualId,
        roleId: action.key === "arc_role" ? null : state.roleId,
      };
    // The workspace keeps EVERYTHING underneath (the restore-on-close invariant); closing strips only itself. Its
    // precedence over the reader/editor is a render concern (App.tsx renders it whenever `view` is set).
    // A page switch keeps the selected role only while STAYING on the roles page (a deep link sets both at once).
    case "openView":
      return { ...state, view: action.view, roleId: action.view === "roles" ? state.roleId : null };
    case "closeView":
      return { ...state, view: null, roleId: null };
    case "selectRole":
      return { ...state, roleId: action.id };
  }
}

/** Stale-cleanups + tab clicks REPLACE the current entry (no spurious history); real navigations PUSH a new entry. An
 *  explicit `openManuals({ replace: true })` also replaces — the reader's exit collapses its own entry. */
export function isReplaceAction(action: NavAction): boolean {
  return (
    action.type === "setSearchTab" ||
    action.type === "dropStale" ||
    (action.type === "openManuals" && action.replace === true) ||
    (action.type === "openManualDetails" && action.replace === true) ||
    action.type === "closeView" ||
    (action.type === "openView" && action.replace === true) ||
    action.type === "selectRole"
  );
}

/**
 * Serialise a {@link NavState} back onto the CURRENT query string — set/delete EVERY managed key (one `put` per field:
 * a field without a `put` would leak its param), preserving any unknown param (defensive; there are none today). Returns
 * a leading-`?` query, or "" when empty (a clean root URL).
 */
export function serializeNavState(state: NavState, currentSearch: string): string {
  const p = new URLSearchParams(currentSearch);
  const put = (key: string, value: string | number | null) =>
    value === null || value === "" ? p.delete(key) : p.set(key, String(value));
  put("arc_fac_selected", state.selectedId);
  put("arc_facility", state.manualsId);
  put("arc_manual", state.openManualId);
  put("arc_edit", state.editManualId);
  put("arc_details", state.detailsManualId);
  put("arc_search", state.searchQuery);
  put("arc_stab", state.searchTab);
  put("arc_view", state.view);
  put("arc_role", state.roleId);
  const qs = p.toString();
  return qs ? `?${qs}` : "";
}

/** The clean URL of a navigation target, for real links (open in new tab): no current search or drill state is carried. */
function hrefOf(action: NavAction): string {
  return serializeNavState(navReducer(parseNavState(""), action), "") || "/";
}
export const facilityDrillHref = (id: number): string => hrefOf({ type: "drillTo", id });
export const facilityManualsHref = (id: number): string => hrefOf({ type: "openManuals", id, replace: false });
export const manualDetailsHref = (facilityId: number, manualId: number): string =>
  hrefOf({ type: "openManualDetails", facilityId, manualId, replace: false });
